<template>
  <div class="map-canvas-shell">
    <div ref="map" class="map-canvas" :class="`tool-${activeTool}`"></div>
    <div v-if="swipeEnabled" class="swipe-control" aria-label="Imagery comparison position">
      <span>Satellite</span>
      <input aria-label="Imagery comparison position" :value="swipePosition" type="range" min="0" max="1" step="0.01" @input="$emit('swipe-position', Number($event.target.value))">
      <span>OSM</span>
    </div>
    <div v-if="osmVisible" class="osm-attribution">&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap contributors</a></div>
  </div>
</template>

<script>
import { CesiumAdapter } from '../core/cesium-adapter'

export default {
  name: 'MapCanvas',
  props: {
    layers: { type: Array, required: true },
    mapState: { type: Object, required: true },
    selection: { type: Object, required: true },
    activeTool: { type: String, default: 'pan' },
    measurement: { type: Object, default: () => ({ coordinates: [] }) },
    sceneMode: { type: String, default: '3d' },
    swipeEnabled: { type: Boolean, default: false },
    swipePosition: { type: Number, default: 0.5 }
  },
  data: () => ({ adapter: null, ready: false }),
  computed: {
    osmVisible() { return this.layers.some(layer => layer.id === 'basemap-osm' && layer.visible) }
  },
  watch: {
    layers: { deep: true, handler() { this.sync() } },
    selection: { deep: true, handler(value) { if (this.adapter) this.adapter.applySelection(value) } },
    measurement: { deep: true, handler(value) { if (this.adapter) this.adapter.setMeasurement(value) } },
    sceneMode(value) { if (this.adapter) this.adapter.setSceneMode(value) },
    swipeEnabled(value) { if (this.adapter) this.adapter.setSwipe(value, this.swipePosition) },
    swipePosition(value) { if (this.adapter && this.swipeEnabled) this.adapter.setSwipe(true, value) }
  },
  mounted() {
    this.adapter = new CesiumAdapter(
      this.$refs.map,
      this.mapState,
      payload => {
        if (this.activeTool === 'select' || this.activeTool === 'identify') this.$emit('feature-click', payload)
      },
      lngLat => this.$emit('pointer-move', lngLat),
      payload => this.$emit('map-click', payload)
    )
    this.adapter.setMapChangeHandler(state => this.$emit('map-change', state))
    this.adapter.setBasemapStatusHandler(status => this.$emit('basemap-status', status))
    this.ready = true
    this.sync()
    this.adapter.applySelection(this.selection)
    this.adapter.setMeasurement(this.measurement)
    this.adapter.setSceneMode(this.sceneMode)
    this.adapter.setSwipe(this.swipeEnabled, this.swipePosition)
    this.$emit('ready')
  },
  beforeDestroy() { if (this.adapter) this.adapter.destroy() },
  methods: {
    sync() { if (this.ready) this.adapter.syncLayers(this.layers) },
    zoomToLayer(layer) { if (this.adapter) this.adapter.fitLayer(layer) },
    zoomIn() { this.adapter && this.adapter.zoomIn() },
    zoomOut() { this.adapter && this.adapter.zoomOut() },
    resetNorth() { this.adapter && this.adapter.resetNorth() },
    flyHome() { this.adapter && this.adapter.flyHome() },
    locateUser() { return this.adapter ? this.adapter.locateUser() : Promise.reject(new Error('The globe is still loading.')) },
    resize() { this.adapter && this.adapter.resize() }
  }
}
</script>

<style scoped>
.map-canvas-shell, .map-canvas { height: 100%; width: 100%; }
.map-canvas-shell { background: #09131b; position: relative; }
.tool-select, .tool-identify, .tool-box-select, .tool-measure-distance, .tool-measure-area { cursor: crosshair; }
.swipe-control { align-items: center; background: #071018de; border: 1px solid #486278; bottom: 11px; color: #c6d9eb; display: flex; font: 10px "DM Mono", monospace; gap: 7px; left: 50%; padding: 5px 8px; position: absolute; transform: translateX(-50%); z-index: 9; }
.swipe-control input { accent-color: #65b6ff; cursor: ew-resize; width: 180px; }
.osm-attribution { background: #071018d9; bottom: 38px; color: #c6d9eb; font: 10px "SFMono-Regular", Consolas, monospace; padding: 3px 5px; position: absolute; right: 10px; z-index: 8; }.osm-attribution a { color: inherit; }.osm-attribution a:hover { color: #fff; }
</style>
